import type { MenuDTOTypes } from '@repo/experience-api-client';
import type { ReactNode } from 'react';
import { Box, Col, Grid, Space } from '@gousto-internal/citrus-react';
import {
  MENU_TABPANEL_ID,
  MenuCollectionFilters,
} from '../MenuCollectionFilters/MenuCollectionFilters';
import { useMenuFilters } from '../../hooks/useMenuFilters';
import { MenuGrid, MenuGridItem } from '../MenuGrid/MenuGrid';
import { MenuDietaryFilters } from '../MenuDietaryFilters/MenuDietaryFilters';
import { MenuEmptyState } from '../MenuEmptyState/MenuEmptyState';
import { ProductFeaturesCard } from '../ProductFeaturesCard/ProductFeaturesCard';

type MenuWithFiltersProps = {
  collections: MenuDTOTypes.MenuCollection[];
  dietaryCollections: MenuDTOTypes.MenuDietaryCollection[];
  recipes: MenuDTOTypes.MenuRecipe[];
  children: (recipes: MenuDTOTypes.MenuRecipe[]) => ReactNode;
  showProductFeaturesCard: boolean;
};

export function MenuWithFilters(props: MenuWithFiltersProps) {
  const { collections, dietaryCollections, recipes, children, showProductFeaturesCard } = props;

  const {
    activeCollectionSlug,
    activeDietaryCollectionsSlugs,
    activeDietaryCollectionsWithCounts,
    sortedCollections,
    onChooseCollection,
    filteredRecipes,
    activeCollectionName,
    onDietaryToggle,
    onResetDietaryFilters,
  } = useMenuFilters(recipes, collections, dietaryCollections);

  const shouldShowCollectionFilters = Boolean(collections.length);
  const showEmptyState = shouldShowCollectionFilters && filteredRecipes.length === 0;

  return (
    <>
      {shouldShowCollectionFilters && (
        <>
          <Space size={[0, null, 6]} />
          <Grid paddingH={[0, null, 6]}>
            <Col size={12}>
              <MenuCollectionFilters
                collections={sortedCollections}
                activeCollectionSlug={activeCollectionSlug}
                onCollectionClick={onChooseCollection}
                recipeContainerId={MENU_TABPANEL_ID}
              />
              <MenuDietaryFilters
                onReset={onResetDietaryFilters}
                onToggleDietaryCollection={onDietaryToggle}
                dietaryCollections={activeDietaryCollectionsWithCounts}
                dietaryCollectionsState={activeDietaryCollectionsSlugs}
              />
            </Col>
          </Grid>
          <Box
            role="tabpanel"
            id={MENU_TABPANEL_ID}
            aria-label={`Recipes in ${activeCollectionName} collection`}
          >
            {showEmptyState ? (
              <Grid paddingH={[4, 4, 6]} paddingV={[4, 4, 6]}>
                <Col size={12}>
                  <MenuEmptyState />
                </Col>
              </Grid>
            ) : (
              <MenuGrid>
                {showProductFeaturesCard && (
                  <MenuGridItem>
                    <ProductFeaturesCard />
                  </MenuGridItem>
                )}

                {children(filteredRecipes)}
              </MenuGrid>
            )}
          </Box>
        </>
      )}
    </>
  );
}
